<template>
  <div
    class="flex justify-start text-gray-500"
    :class="{ '-ml-2 pl-1': truncate }"
    @click.stop
  >
    <label
      v-tooltip="labelTooltip ? labelTooltip : undefined"
      class="flex font-medium"
      :title="option"
      :class="[
        labelClass,
        { 'overflow-x-hidden': truncate },
        disabled ? 'text-gray-400' : 'cursor-pointer',
      ]"
    >
      <input
        type="checkbox"
        :class="[
          { 'mr-4': Object.keys($slots).length && !noMargin },
          { 'cursor-pointer': !disabled },
          { 'text-indigo-600': !disabled },
          { 'text-gray-300': disabled },
          { 'mt-1 mb-1 ml-1': truncate },
        ]"
        class="h-4 w-4 place-self-center rounded-xs border-gray-300"
        :name="name"
        :value="value"
        :checked="isChecked"
        :disabled="disabled"
        :id="String(id)"
        role="checkbox"
        :data-cy="`${name}-checkbox`"
        :indeterminate="indeterminate"
        @change="onChange"
      />
      <span v-if="truncate" class="overflow-hidden text-ellipsis">
        <slot></slot>
      </span>
      <slot v-else></slot>
    </label>
  </div>
</template>

<script setup lang="ts" generic="TValue, TModelValue extends boolean | any[]">
import { computed } from "vue";

import { useField } from "vee-validate";

const {
  name,
  value,
  modelValue,
  disabled,
  id = "",
  option = "",
  labelClass = "",
  truncate,
  noMargin,
  indeterminate,
  labelTooltip,
} = defineProps<{
  name: string;
  value: TValue;
  modelValue: TModelValue;
  disabled?: boolean;
  id?: string | number;
  truncate?: boolean;
  testId?: string;
  option?: string;
  noMargin?: boolean;
  indeterminate?: boolean;
  labelClass?: string;
  labelTooltip?: string;
}>();

const emit = defineEmits<{
  "update:modelValue": [TModelValue];
}>();

const { handleChange } = useField(name, undefined, {
  type: "checkbox",
  valueProp: value,
});

const isChecked = computed(() => {
  if (Array.isArray(modelValue)) {
    return modelValue.includes(value);
  }
  return !!modelValue;
});

const onChange = (event: Event) => {
  const isChecked = (event.target as HTMLInputElement).checked;

  if (!Array.isArray(modelValue)) {
    handleChange(isChecked);
    emit("update:modelValue", isChecked as TModelValue);
  } else {
    const newValue = [...modelValue];

    if (isChecked) {
      newValue.push(value);
    } else {
      newValue.splice(newValue.indexOf(value), 1);
    }

    handleChange(value);
    emit("update:modelValue", newValue as TModelValue);
  }
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

@reference "@/assets/tailwind.css";

input[type="checkbox"] {
  &:indeterminate {
    @apply relative h-4 w-4 border-gray-400;
    content: "";
    background: none;

    &::after {
      @apply absolute top-1/2 left-1/2 h-2-5 w-2-5 -translate-x-1/2 -translate-y-1/2 transform bg-indigo-600;
      content: "";
    }
  }
}
</style>
